import { Button } from '@barfacil/barfacil-ui'
import {
  Calculator,
  CalendarHeart,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ClipboardList,
  House,
  Pencil,
  Settings,
} from 'lucide-react'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom'

import { Logo } from '@/app/panel/_components/logo'
import { useNavbarContext } from '@/app/panel/_contexts/navbar-context'
import { screenSize } from '@/config/screens'
import { Featured } from '@/contexts/featured-context'
import { useClickOutside } from '@/hooks/use-click-outside'
import { useWindowSize } from '@/hooks/use-window-size'
import { cn } from '@/lib/utils'
import { Feature } from '@/schemas/feature'

type NavRouteChildren = {
  element: React.FC
  parent?: boolean
}

type NavRouteObject = {
  element: React.FC<PropsWithChildren>
  children: NavRouteChildren[]
}

type NavbarItem = NavLinkProps &
  React.RefAttributes<HTMLAnchorElement> & {
    icon: React.ReactNode
    label: string
    hasChildren?: boolean
  }

type NavbarItemChildren = Omit<NavbarItem, 'icon' | 'hasChildren'>

const NavItem = ({
  icon,
  label,
  hasChildren,
  className,
  ...props
}: NavbarItem) => {
  const { expanded } = useNavbarContext()

  return (
    <NavLink
      className={({ isActive }) =>
        cn(
          'relative block rounded text-sm font-semibold text-gray-25 outline-none ring-0 ring-offset-0 hover:text-green-500 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2',
          isActive &&
            'peer/is-active text-gray-25 before:absolute before:-left-4 before:top-0 before:h-full before:w-1.5 before:rounded-r before:bg-green-500 hover:text-gray-25',
          isActive && !expanded && 'before:-left-8',
          expanded ? 'ml-4 mr-2' : 'mx-8',
          className,
        )
      }
      {...props}
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'relative flex items-center gap-x-4 overflow-x-hidden rounded-md p-2 transition-colors',
              hasChildren && isActive && expanded && 'rounded-b-none',
              isActive && 'bg-green-500',
              !expanded && 'px-[0.65rem]',
            )}
          >
            <span>{icon}</span>

            <span className="line-clamp-1">{label}</span>

            {hasChildren && (
              <span className="ml-auto translate-y-0.5">
                <ChevronDown
                  className={cn(
                    'transition-transform',
                    isActive && 'rotate-180',
                  )}
                  size={20}
                />
              </span>
            )}
          </span>
        </>
      )}
    </NavLink>
  )
}

const NavItemChildren = ({ label, ...props }: NavbarItemChildren) => (
  <li className="group">
    <NavLink
      className={({ isActive }) =>
        cn(
          "relative flex w-full items-center gap-x-6 bg-blue-300 px-2 py-3 pl-4 text-xs font-medium text-gray-25 ring-0 before:content-['•'] hover:text-green-500 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 group-last:rounded-b-md",
          isActive && 'bg-blue-400 text-green-500',
        )
      }
      {...props}
    >
      <span className="line-clamp-1">{label}</span>
    </NavLink>
  </li>
)

const DashboardNav = () => {
  return <NavItem icon={<House size={20} />} label="Dashboard" to="dashboard" />
}

const ComandasNav = () => {
  return (
    <NavItem
      icon={<ClipboardList size={20} />}
      label="Comandas"
      to="comandas"
    />
  )
}

const EventsNav = () => {
  return (
    <NavItem icon={<CalendarHeart size={20} />} label="Eventos" to="eventos" />
  )
}

const RegistrationsNav = () => {
  return (
    <NavItem
      icon={<Pencil size={20} />}
      label="Cadastros"
      to="cadastros"
      hasChildren
    />
  )
}

const ManagementPriceListNav = () => (
  <NavItemChildren label="Lista de preços" to="/cadastros/lista-de-precos" />
)

const ManagementLayoutNav = () => (
  <NavItemChildren label="Layout" to="/cadastros/layout" />
)

const ManagementEventsNav = () => (
  <NavItemChildren label="Eventos" to="/cadastros/eventos" />
)

const TerminalsNav = () => {
  return (
    <NavItem
      icon={<Calculator size={20} />}
      label="Terminais"
      to="terminais"
      hasChildren
    />
  )
}

const TerminalsSettingsNav = () => (
  <NavItemChildren label="Opções do setor" to="/terminais/opcoes" />
)

const SettingsNav = () => {
  return (
    <NavItem
      icon={<Settings size={20} />}
      label="Configurações"
      to="configuracoes"
      hasChildren
    />
  )
}

const SettingsTokenNav = () => (
  <NavItemChildren label="Token" to="/configuracoes/token" />
)

const SettingsCompanyNav = () => (
  <NavItemChildren label="Empresa" to="/configuracoes/empresa" />
)

const navItems: NavRouteObject[] = [
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => <DashboardNav />,
      },
    ],
  },
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => <ComandasNav />,
      },
    ],
  },
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => <EventsNav />,
      },
    ],
  },
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => (
          <Featured feature={Feature['Navbar.Management']}>
            <RegistrationsNav />
          </Featured>
        ),
      },
      {
        element: () => (
          <Featured feature={Feature['Navbar.Management.PriceList']}>
            <ManagementPriceListNav />
          </Featured>
        ),
      },
      {
        element: () => (
          <Featured feature={Feature['Navbar.Management.Layout']}>
            <ManagementLayoutNav />
          </Featured>
        ),
      },
      {
        element: () => (
          <Featured feature={Feature['Navbar.Management.Events']}>
            <ManagementEventsNav />
          </Featured>
        ),
      },
    ],
  },
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => (
          <Featured feature={Feature['Navbar.Terminals']}>
            <TerminalsNav />
          </Featured>
        ),
      },
      {
        element: () => (
          <Featured feature={Feature['Navbar.Terminals.Settings']}>
            <TerminalsSettingsNav />
          </Featured>
        ),
      },
    ],
  },
  {
    element: ({ children }) => <>{children}</>,
    children: [
      {
        parent: true,
        element: () => <SettingsNav />,
      },
      {
        element: () => <SettingsTokenNav />,
      },
      {
        element: () => <SettingsCompanyNav />,
      },
    ],
  },
]

const Navbar = () => {
  const { expanded, setExpanded } = useNavbarContext()
  const { width } = useWindowSize()

  const location = useLocation()
  const navbarRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (width < screenSize.LG) {
      setExpanded(false)
    }
  }, [location, width, setExpanded])

  useClickOutside(
    navbarRef,
    () => setExpanded(false),
    expanded && width < screenSize.LG,
  )

  return (
    <nav
      className={cn(
        'fixed left-0 top-0 z-10 h-full overflow-hidden bg-blue-500 shadow-[0_1.25rem_1.875rem_0] shadow-gray-400/40 duration-300 lg:transition-[width]',
        expanded
          ? 'w-56'
          : 'w-56 -translate-x-56 lg:w-[6.5rem] lg:translate-x-0',
      )}
      ref={navbarRef}
    >
      <div className="mb-8 border-b border-green-500 px-8 py-3">
        <Logo />
      </div>

      <h2
        className={cn(
          'mb-3 font-medium text-gray-25',
          expanded ? 'px-4' : 'px-8',
        )}
      >
        Menu
      </h2>

      <ul className="flex flex-col gap-y-1">
        {navItems.map(({ element: Element, children }, index) => (
          <Element key={index}>
            <li>
              {children
                ?.filter(({ parent }) => parent)
                .map(({ element: ElementChild }, index) => (
                  <ElementChild key={index} />
                ))}

              {(expanded || width < screenSize.LG) &&
                children?.filter(({ parent }) => !parent).length > 0 && (
                  <ul className="ml-4 mr-2 hidden peer-[&]/is-active:block">
                    {children
                      ?.filter(({ parent }) => !parent)
                      .map(({ element: ElementChild }, index) => (
                        <ElementChild key={index} />
                      ))}
                  </ul>
                )}
            </li>
          </Element>
        ))}
      </ul>

      <div
        className={cn(
          'absolute bottom-[5%] w-full text-right',
          expanded ? 'px-2' : 'px-8',
        )}
      >
        <Button
          className="size-10 p-1 text-gray-25 hover:bg-gray-25/10"
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="sr-only">
            {expanded ? 'Minimizar navegação' : 'Maximizar navegação'}
          </span>

          {expanded ? <ChevronFirst size={24} /> : <ChevronLast size={24} />}
        </Button>
      </div>
    </nav>
  )
}

export { Navbar }
